import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchPublicSettings } from '../Api/settingsApi';
import { useStorefront } from './StoreContext';
import { formatMoney, parseCurrencyFromSettings, setActiveCurrency } from '../Utils/currencyFormat';
import KineticLoader from '../Component/UI/KineticLoader/KineticLoader';

const PublicSettingsContext = createContext(null);

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const SETTINGS_CACHE_PREFIX = 'aaa_storefront_public_settings_retail:';

function readCachedSettings(storeId) {
    if (!storeId) return null;
    try {
        const raw = localStorage.getItem(`${SETTINGS_CACHE_PREFIX}${storeId}`);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
}

function writeCachedSettings(storeId, data) {
    if (!storeId || !data) return;
    try {
        localStorage.setItem(`${SETTINGS_CACHE_PREFIX}${storeId}`, JSON.stringify(data));
    } catch {
        /* private mode / blocked storage / quota */
    }
}

export function PublicSettingsProvider({ children }) {
    const { store, ready } = useStorefront();
    const storeId = store?.id || null;
    // Last saved copy for this store — lets repeat visits render the merchant's data immediately.
    const cachedSettings = useMemo(() => readCachedSettings(storeId), [storeId]);
    const [fetched, setFetched] = useState({ storeId: null, data: null, settled: false });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isCurrentStore = fetched.storeId === storeId;
    const publicSettings = (isCurrentStore && fetched.data) || cachedSettings;
    const settled = isCurrentStore && fetched.settled;

    const loadSettings = useCallback(
        async (isBackground = false) => {
            if (!storeId) return;

            try {
                if (!isBackground) setLoading(true);
                setError(null);
                const data = await fetchPublicSettings(storeId);
                setFetched({ storeId, data, settled: true });
                writeCachedSettings(storeId, data);
            } catch (err) {
                console.error('Error fetching public settings:', err);
                setError(err);
                // Keep whatever we already had; just mark the first load as finished so the page renders.
                setFetched((prev) => ({
                    storeId,
                    data: prev.storeId === storeId ? prev.data : null,
                    settled: true,
                }));
            } finally {
                if (!isBackground) setLoading(false);
            }
        },
        [storeId],
    );

    useEffect(() => {
        if (!ready || !storeId) return undefined;

        loadSettings();
        const interval = setInterval(() => loadSettings(true), REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [ready, storeId, loadSettings]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) loadSettings(true);
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [loadSettings]);

    const { currencyCode, currencySymbol, storeLocale, formatPrice } = useMemo(() => {
        // Store settings are authoritative, but they arrive a request later than the store
        // itself — until then price the catalogue in the currency the resolve call reported
        // rather than flashing a wrong symbol.
        const source = publicSettings || (store?.currency ? { currency: store.currency } : null);
        const { code, symbol, locale } = parseCurrencyFromSettings(source);
        const moneyOpts = { fallbackSymbol: symbol, locale };

        // Mirrored into module state for the pure helpers that cannot call a hook.
        setActiveCurrency({ code, symbol, locale });

        return {
            currencyCode: code,
            currencySymbol: symbol,
            storeLocale: locale,
            formatPrice: (amount) => formatMoney(amount, code, moneyOpts),
        };
    }, [publicSettings, store?.currency]);

    const value = useMemo(
        () => ({
            publicSettings,
            loading,
            error,
            refetch: () => loadSettings(false),
            currencyCode,
            currencySymbol,
            storeLocale,
            formatPrice,
        }),
        [publicSettings, loading, error, loadSettings, currencyCode, currencySymbol, storeLocale, formatPrice],
    );

    // First visit (nothing cached yet): hold the page until the merchant's settings arrive,
    // so the built-in logo/name and default page toggles never flash.
    if (storeId && !publicSettings && !settled) {
        return (
            <div role="status" aria-label="Loading store…">
                <KineticLoader />
            </div>
        );
    }

    return <PublicSettingsContext.Provider value={value}>{children}</PublicSettingsContext.Provider>;
}

export function usePublicSettings() {
    const ctx = useContext(PublicSettingsContext);
    if (!ctx) {
        throw new Error('usePublicSettings must be used within PublicSettingsProvider');
    }
    return ctx;
}
